<template>
  <div>
    <Listbox
      :model-value="selectedItems"
      :open="isOpen"
      @update:model-value="(value) => handleSelect(value)"
    >
      <div class="relative mt-1">
        <Transition name="slide-fade">
          <ListboxLabel
            v-if="showLabel"
            class="absolute -top-5 text-xs leading-5 font-medium text-gray px-2"
          >
            {{ label }}
          </ListboxLabel>
        </Transition>

        <ListboxButton
          class="flex relative w-full cursor-default rounded-lg bg-light py-2.5 pl-3 rtl:pl-10 pr-10 rtl:pr-3 text-left shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm z-20"
        >
          <span
            class="pointer-events-none absolute inset-y-0 right-0 rtl:right-auto left-auto rtl:left-0 flex items-center px-2"
          >
            <ChevronUpDownIcon
              class="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </span>
          <span class="block truncate" :class="{ 'text-zinc-400': !showLabel }">
            {{ selectedLabel }}
          </span>
        </ListboxButton>

        <transition
          leave-active-class="transition duration-100 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <ListboxOptions
            class="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white pb-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-30"
          >
            <MultiselectOptionsFilter v-model="optionsFilter" />
            <ListboxOption
              v-slot="{ active }"
              v-for="item in filteredItems"
              :key="item?.[labelName]"
              :value="item"
              as="template"
            >
              <li
                :class="[
                  active ? 'bg-amber-100 text-amber-900' : 'text-gray-900',
                  'relative cursor-default select-none py-2 pl-10 pr-4',
                ]"
              >
                <span
                  :class="[
                    isSelected(item) ? 'font-medium' : 'font-normal',
                    'block truncate',
                  ]"
                  >{{ item?.[labelName] }}</span
                >
                <span
                  v-if="isSelected(item)"
                  class="absolute inset-y-0 left-0 flex items-center pl-3 text-main"
                >
                  <CheckIcon class="h-5 w-5" aria-hidden="true" />
                </span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import {
  Listbox,
  ListboxLabel,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from '@headlessui/vue';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid';
import { isTemplateSpan } from 'typescript';

const props = withDefaults(
  defineProps<{
    items: { [x: string]: string | number }[];
    labelName?: string;
    valueName?: string;
    label?: string;
    selectedLabel?: string;
  }>(),
  {
    labelName: 'Text',
    valueName: 'Value',
    selectedLabel: 'Select Items',
  }
);

const emits = defineEmits<{
  update: [selectedItems: { [x: string]: string | number }[]];
}>();

const optionsFilter = ref('');
const filteredItems = computed(() =>
  props.items.filter((i) =>
    (i[props.labelName] as string).startsWith(optionsFilter.value)
  )
);
const showLabel = computed(() => props.label && selectedItems.value.length > 0);

const isOpen = ref(false);
const selectedItems = ref<{ [x: string]: string | number }[]>([]);

const selectedLabel = computed(() => {
  if (!Array.isArray(selectedItems.value) || selectedItems.value.length === 0)
    return props.label;
  if (selectedItems.value.length < 3)
    return selectedItems.value
      .map((item) => item?.[props.labelName])
      .join(', ');
  return `${props.selectedLabel} (${selectedItems.value.length})`;
});

function isSelected(value: { [x: string]: string | number }) {
  return selectedItems.value?.find(
    (item) => item[props.valueName] === value[props.valueName]
  )
    ? true
    : false;
}

function handleSelect(value: { [x: string]: string | number }) {
  !isSelected(value)
    ? selectedItems.value?.push(value)
    : (selectedItems.value = selectedItems.value?.filter(
        (item) => item[props.valueName] !== value[props.valueName]
      ));
  isOpen.value = true;

  emits('update', selectedItems.value);
}
</script>
<style scoped>
.slide-fade-enter-active {
  transition: all 0.4s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.5s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(20px);
  opacity: 0;
}
</style>
